/**
 * RAIN_APPROACHES weather progression (Phaser-free, unit-tested). Once the flag is set the scene
 * runs a simulated clock and applies each phase exactly once:
 *   sultry → wind (set by the gh.rain timeline) → drizzle → rain, ≈ 60 s in total.
 * A save/URL entry that already carries a later weather state starts the clock at that phase so
 * the sky never goes backwards on resume.
 */
import type { WeatherState } from '@/core/types';

export interface RainPhase { at: number; state: WeatherState; transitionMs: number }

export const RAIN_PHASES: readonly RainPhase[] = [
  { at: 0, state: 'wind', transitionMs: 4000 },
  { at: 18_000, state: 'drizzle', transitionMs: 7000 },
  { at: 40_000, state: 'rain', transitionMs: 14_000 },
];

/** Clock value at which the last phase has fully settled. */
export const RAIN_TOTAL_MS = RAIN_PHASES[RAIN_PHASES.length - 1]!.at + RAIN_PHASES[RAIN_PHASES.length - 1]!.transitionMs;

/** Index of the phase in effect at `clockMs` (-1 before the first). */
export function phaseIndexAt(clockMs: number): number {
  let idx = -1;
  RAIN_PHASES.forEach((p, i) => { if (clockMs >= p.at) idx = i; });
  return idx;
}

/** Where the clock starts when the scene is entered with `state` already in effect. */
export function clockForWeather(state: WeatherState): number {
  const i = RAIN_PHASES.findIndex((p) => p.state === state);
  if (i < 0) return state === 'interior-rain' ? RAIN_TOTAL_MS : 0;
  return RAIN_PHASES[i]!.at;
}

/** Phases whose `at` lies in (prev, now] — the ones to apply when the clock advances from prev to now. */
export function phasesCrossed(prevMs: number, nowMs: number): RainPhase[] {
  return RAIN_PHASES.filter((p) => p.at > prevMs && p.at <= nowMs);
}
